import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addDays, isBefore, startOfDay } from 'date-fns';
import { useRouter } from 'next/router';
import { CURRENCY_USD } from 'constants/currency';
import {
  CompetitionGenderEnum,
  EventGroupFormatsEnum,
  SportsEnum,
  TeamTypesEnum,
  useGetFeaturedTournamentBoostsQuery,
} from 'types/generated/client';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { isWithinPriceRange } from 'utils/shared/money/isWithinPriceRange';
import { resolveVenueTimeZone } from 'utils/shared/time/venueDayFilters';
import { useGeoLocation } from 'hooks/useGeoLocation';
import { useInView } from 'hooks/useInView';
import { useModal } from 'hooks/useModal';
import { GoogleMapProps } from 'components/GoogleMap/props';
import TournamentFilters from './TournamentFilters';
import TournamentList from './TournamentList';
import TournamentMapMarker from './TournamentMapMarker';
import { FeaturedTournament, TournamentHookProps } from './props';
import {
  INITIAL_PAGE_SIZE,
  MAXIMUM_AGE,
  MAXIMUM_COST,
  MAX_FEATURED_TOURNAMENTS,
  MINIMUM_AGE,
  MINIMUM_COST,
  MONEYBALL_FORMAT_ID,
  TOURNAMENT_DISTANCE_IMPERIAL_OPTIONS,
  type TournamentTypeFilter,
  formatDateRange,
  matchesTournamentType,
} from './utils';

const useTournament = ({ tournaments }: TournamentHookProps) => {
  const {
    position,
    getEstimatedLocation,
    requestUserLocation,
    hasLocationPermission,
    centerLatitude,
    centerLongitude,
    onMapDrag,
    isExactCenter,
  } = useGeoLocation();
  const { closeModal, openModal, isOpen: isModalOpen } = useModal();
  const router = useRouter();
  const selectedSport = useMemo<SportsEnum | null>(() => {
    const raw = router.query.sport;
    if (!raw) return null;
    return (
      Object.values(SportsEnum).find((val) => val.toLowerCase() === String(raw).toLowerCase()) ??
      null
    );
  }, [router.query.sport]);
  const discoverContainerRef = useRef<HTMLDivElement | null>(null);
  const [isOpenMap, setIsOpenMap] = useState(false);
  const isDiscoverSectionInView = useInView(discoverContainerRef);
  const [isPageLoaded, setIsPageLoaded] = useState(false);

  const [activeTournamentId, setActiveTournamentId] = useState<string | null>(null);
  const [listHoveredTournamentId, setListHoveredTournamentId] = useState<string | null>(null);

  const [distance, setDistance] = useState(TOURNAMENT_DISTANCE_IMPERIAL_OPTIONS[0]);

  const [selectedType, setSelectedType] = useState<TournamentTypeFilter>('ALL');
  const [isPrizeMoney, setIsPrizeMoney] = useState(false);
  const [costMinimum, setCostMinimum] = useState(MINIMUM_COST);
  const [costMaximum, setCostMaximum] = useState(MAXIMUM_COST);
  const [ageMinimum, setAgeMinimum] = useState(MINIMUM_AGE);
  const [ageMaximum, setAgeMaximum] = useState(MAXIMUM_AGE);
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [teamType, setTeamType] = useState<TeamTypesEnum | string | null>(null);
  const [competitionFormat, setCompetitionFormat] = useState<EventGroupFormatsEnum | string | null>(
    null,
  );
  const [competitionGender, setCompetitionGender] = useState<CompetitionGenderEnum | null>(null);

  const featuredNow = useMemo(() => new Date().toISOString(), []);
  const { data: featuredData } = useGetFeaturedTournamentBoostsQuery({
    variables: { now: featuredNow },
    fetchPolicy: 'cache-and-network',
  });

  const decoratedTournaments = useMemo(() => {
    let processedTournaments = (tournaments?.events || []).map((tournament) => {
      return {
        ...tournament,
        startTimestamp: new Date(tournament.startDateTime).getTime(),
        displayDate: formatDateRange(
          new Date(tournament.startDateTime),
          new Date(tournament.endDateTime),
          // City is the last resort for external imports that carry no venue.
          resolveVenueTimeZone(tournament) || tournament.city?.timezone,
        ),
        distance: Math.round(
          calculateHaversineDistance({
            coord1: {
              latitude: tournament.latitude,
              longitude: tournament.longitude,
            },
            coord2: {
              latitude: centerLatitude,
              longitude: centerLongitude,
            },
            unit: 'miles',
          }),
        ),
      };
    });

    return processedTournaments;
  }, [tournaments, centerLatitude, centerLongitude]);

  const featuredTournaments = useMemo<FeaturedTournament[]>(() => {
    const boosts = featuredData?.boosts ?? [];

    // Decorate each boosted tournament the same way the main list does, then apply
    // only sport + distance relevance (NOT date — a live boost is always worth an
    // impression). Cast bridges the client boost-event type to TournamentType; the
    // selected fields mirror the marketplace query, so the runtime shape matches.
    const decorated = boosts
      .filter((boost) => boost.event)
      .map((boost) => {
        const event = boost.event!;
        return {
          ...event,
          startTimestamp: new Date(event.startDateTime).getTime(),
          displayDate: formatDateRange(
            new Date(event.startDateTime),
            new Date(event.endDateTime),
            resolveVenueTimeZone(event) || event.city?.timezone,
          ),
          distance: Math.round(
            calculateHaversineDistance({
              coord1: {
                latitude: Number(event.latitude ?? 0),
                longitude: Number(event.longitude ?? 0),
              },
              coord2: { latitude: centerLatitude, longitude: centerLongitude },
              unit: 'miles',
            }),
          ),
          placementId: boost.id,
        };
      }) as unknown as FeaturedTournament[];

    const relevant = decorated.filter((tournament) => {
      if (selectedSport && tournament.sport !== selectedSport) return false;
      if (distance?.id && tournament.distance > distance.id) return false;
      if (!matchesTournamentType(selectedType, tournament.groupFormat)) return false;
      return true;
    });

    // Tournaments within the user's selected date range are the most relevant —
    // show ALL of them, uncapped. Only when none fall in range do we fall back to
    // a small set of other upcoming boosts (query order = startsAt DESC).
    const isWithinSelectedRange = (tournament: FeaturedTournament) => {
      if (!selectedDate || selectedDate <= -1) return false;
      const endOfRange = addDays(startOfDay(new Date()), selectedDate);
      return isBefore(addDays(new Date(tournament.startDateTime), 1), endOfRange);
    };
    const inRange = relevant.filter(isWithinSelectedRange);

    return inRange.length > 0 ? inRange : relevant.slice(0, MAX_FEATURED_TOURNAMENTS);
  }, [
    featuredData,
    selectedSport,
    selectedType,
    distance,
    selectedDate,
    centerLatitude,
    centerLongitude,
  ]);

  const featuredIds = useMemo(
    () => new Set(featuredTournaments.map((tournament) => tournament.id)),
    [featuredTournaments],
  );

  const tournamentsForMarketplace = React.useMemo(() => {
    const filterStart = Date.now();
    let processedTournaments = decoratedTournaments || [];

    if (distance?.id) {
      processedTournaments = processedTournaments.filter(
        (tournament) => tournament.distance <= distance.id,
      );
    }

    if (selectedSport) {
      processedTournaments = processedTournaments.filter(
        (tournament) => tournament.sport === selectedSport,
      );
    }

    if (selectedType !== 'ALL') {
      processedTournaments = processedTournaments.filter((tournament) =>
        matchesTournamentType(selectedType, tournament.groupFormat),
      );
    }

    if (isPrizeMoney) {
      processedTournaments = processedTournaments.filter((tournament) => tournament.hasPrizes);
    }

    /**
     * Gender, team type, format and age are all division-level, so each matches when
     * ANY of the tournament's groups matches — entering a mixed-doubles bracket is
     * reason enough for a mixed-doubles search to surface the tournament.
     *
     * Scraped listings have no groups at all, so they drop out of these four filters
     * rather than passing vacuously: a division filter the source never told us about
     * cannot be claimed as a match.
     */
    if (competitionGender) {
      processedTournaments = processedTournaments.filter((tournament) =>
        tournament.groups.some((group) => group.gender === competitionGender),
      );
    }

    if (teamType) {
      processedTournaments = processedTournaments.filter((tournament) =>
        tournament.groups.some((group) => group.teamType === teamType),
      );
    }

    // Moneyball is a division flag rather than a format, so it cannot be compared
    // against `groupFormat` — see MONEYBALL_FORMAT_ID.
    if (competitionFormat === MONEYBALL_FORMAT_ID) {
      processedTournaments = processedTournaments.filter((tournament) =>
        tournament.groups.some((group) => !!group.isMoneyBall),
      );
    } else if (competitionFormat) {
      // `groupFormat` is the event-level summary of its divisions, so it is checked too —
      // that is what the header's Tournaments/Round Robins switcher reads, and scraped
      // listings carry it without having groups.
      processedTournaments = processedTournaments.filter(
        (tournament) =>
          tournament.groupFormat === competitionFormat ||
          tournament.groups.some((group) => group.format === competitionFormat),
      );
    }

    const isFilteringOnAge = ageMinimum !== MINIMUM_AGE || ageMaximum !== MAXIMUM_AGE;
    if (isFilteringOnAge) {
      // Overlap, not containment: a 30-49 division belongs in a 40-99 search. An
      // unset bound on the division means "open", hence the range defaults.
      processedTournaments = processedTournaments.filter((tournament) =>
        tournament.groups.some((group) => {
          const groupMinimum = group.minimumAge ?? MINIMUM_AGE;
          const groupMaximum = group.maximumAge ?? MAXIMUM_AGE;
          return groupMinimum <= ageMaximum && groupMaximum >= ageMinimum;
        }),
      );
    }

    const isFilteringOnCost = costMinimum !== MINIMUM_COST || costMaximum !== MAXIMUM_COST;
    if (isFilteringOnCost) {
      processedTournaments = processedTournaments.filter((tournament) => {
        const cost = convertUnitPriceToFormattedPrice(
          tournament.registrationPriceUnitAmount || 0,
          tournament.currency || CURRENCY_USD,
        );
        return isWithinPriceRange(cost.priceFormatted, {
          minimum: costMinimum,
          maximum: costMaximum,
          sliderMaximum: MAXIMUM_COST,
        });
      });
    }

    if (selectedDate && selectedDate > -1) {
      const today = startOfDay(new Date());
      const endOfRange = addDays(today, selectedDate);

      processedTournaments = processedTournaments.filter((tournament) => {
        return isBefore(addDays(new Date(tournament.startDateTime), 1), endOfRange);
      });
    }

    const isBounceCreated = (t: { source?: string | null }) => t.source == null;
    const sortBySourceThenDistance = (
      a: (typeof processedTournaments)[0],
      b: (typeof processedTournaments)[0],
    ) => {
      if (isBounceCreated(a) && !isBounceCreated(b)) return -1;
      if (!isBounceCreated(a) && isBounceCreated(b)) return 1;
      return a.distance - b.distance;
    };
    const sortBySourceThenTimestamp = (
      a: (typeof processedTournaments)[0],
      b: (typeof processedTournaments)[0],
    ) => {
      if (isBounceCreated(a) && !isBounceCreated(b)) return -1;
      if (!isBounceCreated(a) && isBounceCreated(b)) return 1;
      return a.startTimestamp - b.startTimestamp;
    };

    const finalTournaments = isPageLoaded
      ? processedTournaments.sort(sortBySourceThenDistance)
      : processedTournaments.sort(sortBySourceThenTimestamp).slice(0, INITIAL_PAGE_SIZE);
    const filterEnd = Date.now();
    const filterDuration = filterEnd - filterStart;

    if (process.env.APP_STAGE !== 'production') {
      console.log('filterDuration', filterDuration);
    }

    return finalTournaments;
  }, [
    decoratedTournaments,
    distance,
    selectedSport,
    selectedType,
    isPrizeMoney,
    competitionGender,
    teamType,
    competitionFormat,
    ageMinimum,
    ageMaximum,
    selectedDate,
    costMinimum,
    costMaximum,
    isPageLoaded,
  ]);

  const { bounceTournaments, scrapedTournaments } = React.useMemo(() => {
    const isBounceCreated = (t: { source?: string | null }) => t.source == null;
    // Boosted tournaments surface only in the featured frame — drop them here.
    const visible = tournamentsForMarketplace.filter((t) => !featuredIds.has(t.id));
    return {
      bounceTournaments: visible.filter(isBounceCreated),
      scrapedTournaments: visible.filter((t) => !isBounceCreated(t)),
    };
  }, [tournamentsForMarketplace, featuredIds]);

  // Featured boosts can be date-filtered out of the main list, so add them back
  // for the map — otherwise the boosted tournament has no marker.
  const mappableTournaments = useMemo(
    () => [
      ...featuredTournaments,
      ...tournamentsForMarketplace.filter((t) => !featuredIds.has(t.id)),
    ],
    [featuredTournaments, tournamentsForMarketplace, featuredIds],
  );

  const activeTournament = mappableTournaments.find(
    (tournament) => tournament.id === activeTournamentId,
  );

  const tournamentMapProps: GoogleMapProps = {
    syncDragToURL: true,
    center: {
      lat: centerLatitude,
      lng: centerLongitude,
    },
    zoom: 12,
    isExactCenter,
    centerIcon: (
      <div className="h-8 w-8 rounded-full border-[12px] border-color-border-brand bg-white" />
    ),
    markers: mappableTournaments.map((tournament) => ({
      lat: tournament.latitude,
      lng: tournament.longitude,
      zIndex:
        activeTournament?.id === tournament.id
          ? 3
          : listHoveredTournamentId === tournament.id
          ? 2
          : 1,
      children: (
        <TournamentMapMarker
          tournament={tournament}
          activeTournament={activeTournament}
          setActiveTournamentId={setActiveTournamentId}
          listHoveredTournamentId={listHoveredTournamentId}
          setListHoveredTournamentId={setListHoveredTournamentId}
        />
      ),
    })),
  };

  const clearTournamentFilters = () => {
    setIsPrizeMoney(false);
    setTeamType(null);
    setCompetitionFormat(null);
    setCompetitionGender(null);
    setSelectedDate(null);
    setAgeMinimum(MINIMUM_AGE);
    setAgeMaximum(MAXIMUM_AGE);
    setCostMinimum(MINIMUM_COST);
    setCostMaximum(MAXIMUM_COST);
    setDistance(TOURNAMENT_DISTANCE_IMPERIAL_OPTIONS[0]);
    setSelectedType('ALL');
  };

  const handleToggleMap = () => {
    setIsOpenMap((curr) => !curr);
  };

  useEffect(() => {
    if (discoverContainerRef.current && isOpenMap) {
      discoverContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [isOpenMap]);

  useEffect(() => {
    if (position) {
      setIsPageLoaded(true);
    }
  }, [position]);

  return {
    TournamentList: (
      <TournamentList
        bounceTournaments={bounceTournaments}
        scrapedTournaments={scrapedTournaments}
        featuredTournaments={featuredTournaments}
        isPageLoaded={isPageLoaded}
        setActiveTournamentId={setActiveTournamentId}
        activeTournamentId={activeTournamentId}
        setListHoveredTournamentId={setListHoveredTournamentId}
        listHoveredTournamentId={listHoveredTournamentId}
        onClearFilters={clearTournamentFilters}
      />
    ),
    TournamentFilters: (
      <TournamentFilters
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        isPrizeMoney={isPrizeMoney}
        setIsPrizeMoney={setIsPrizeMoney}
        costMinimum={costMinimum}
        setCostMinimum={setCostMinimum}
        costMaximum={costMaximum}
        setCostMaximum={setCostMaximum}
        ageMinimum={ageMinimum}
        setAgeMinimum={setAgeMinimum}
        ageMaximum={ageMaximum}
        setAgeMaximum={setAgeMaximum}
        teamType={teamType}
        setTeamType={setTeamType}
        competitionFormat={competitionFormat}
        setCompetitionFormat={setCompetitionFormat}
        competitionGender={competitionGender}
        setCompetitionGender={setCompetitionGender}
        distance={distance}
        setDistance={setDistance}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
      />
    ),
    selectedType,
    setSelectedType,
    tournamentMapProps,
    clearTournamentFilters,
    distance,
    setDistance,
    setIsPageLoaded,
    isModalOpen,
    openModal,
    closeModal,
    isOpenMap,
    handleToggleMap,
    discoverContainerRef,
    isDiscoverSectionInView,
    tournamentsLength: tournamentsForMarketplace.length,
    isPageLoaded,
  };
};

export default useTournament;
